import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Search, Plus, Contact, CheckCircle2, Clock, ArrowRightLeft, Eye, Building,
} from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [reallocFor, setReallocFor] = useState(null);
  const [newPoc, setNewPoc] = useState("");
  const [form, setForm] = useState({ name: "", company: "", poc_user_id: "", status: "Active" });

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients");
    setClients(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
    if (isAdmin) api.get("/users").then((r) => setEmployees(r.data.filter((u) => u.role === "employee")));
  }, []);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      const matchQ = !q || c.name.toLowerCase().includes(q.toLowerCase()) ||
        (c.company || "").toLowerCase().includes(q.toLowerCase()) ||
        (c.poc?.name || "").toLowerCase().includes(q.toLowerCase());
      const matchS = statusFilter === "all" || c.status === statusFilter;
      return matchQ && matchS;
    });
  }, [clients, q, statusFilter]);

  const markFollowup = async (c) => {
    try {
      await api.post("/followups", { client_id: c.id, channel: "Call" });
      toast.success(`Follow-up logged for ${c.name}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const addClient = async () => {
    try {
      await api.post("/clients", { ...form, poc_user_id: form.poc_user_id || null });
      toast.success("Customer added");
      setAddOpen(false);
      setForm({ name: "", company: "", poc_user_id: "", status: "Active" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const reallocate = async () => {
    try {
      await api.put(`/clients/${reallocFor.id}/reallocate`, { poc_user_id: newPoc || null });
      toast.success("Customer reallocated");
      setReallocFor(null); setNewPoc("");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle={isAdmin ? "All customers and their point-of-contact allocation." : "Your allocated customers. Log weekly follow-ups and add notes."}
        testId="customers-page"
        actions={isAdmin && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="add-customer-button">
                <Plus className="h-4 w-4" /> Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Add Customer</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Customer name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="customer-name-input" /></div>
                <div><Label>Company</Label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Assign POC</Label>
                  <Select value={form.poc_user_id} onValueChange={(v) => setForm({ ...form, poc_user_id: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="customer-poc-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Active", "Ongoing", "Inactive", "Dead"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <DialogFooter>
                <Button onClick={addClient} disabled={!form.name} className="rounded-sm" data-testid="save-customer-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search customer, company or POC…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm" data-testid="customer-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["Active", "Ongoing", "Inactive", "Dead"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-secondary/50 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {filtered.length} customer{filtered.length !== 1 ? "s" : ""}
        </div>
        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6"><Empty icon={Contact} title="No customers found" sub="Try adjusting your search or filter." /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Customer</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide hidden md:table-cell">Company</TableHead>
                  {isAdmin && <TableHead className="text-xs font-bold uppercase tracking-wide">POC</TableHead>}
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Status</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Follow-up</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className="hover:bg-secondary/40" data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-semibold max-w-[220px]">
                      <span className="line-clamp-1">{c.name}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{c.company || "—"}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-sm">
                        {c.poc ? c.poc.name : <span className="text-muted-foreground italic">Unassigned</span>}
                      </TableCell>
                    )}
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell>
                      {c.followup_done ? (
                        <span className="inline-flex items-center gap-1 text-success text-xs font-bold"><CheckCircle2 className="h-3.5 w-3.5" /> Done</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[hsl(45_100%_32%)] text-xs font-bold"><Clock className="h-3.5 w-3.5" /> Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!isAdmin && !c.followup_done && (
                        <Button size="sm" variant="outline" className="rounded-sm mr-1 h-8" onClick={() => markFollowup(c)} data-testid={`followup-${c.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Log
                        </Button>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="rounded-sm mr-1 h-8"
                          onClick={() => { setReallocFor(c); setNewPoc(c.poc?.id || ""); }} data-testid={`reallocate-${c.id}`}>
                          <ArrowRightLeft className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" className="rounded-sm h-8" onClick={() => nav(`/customers/${c.id}`)} data-testid={`view-customer-${c.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Reallocate dialog */}
      <Dialog open={!!reallocFor} onOpenChange={(o) => !o && setReallocFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reallocate customer</DialogTitle></DialogHeader>
          {reallocFor && (
            <div className="space-y-4 py-2">
              <div className="border border-border rounded-sm p-3 bg-secondary/40">
                <p className="font-semibold flex items-center gap-2"><Building className="h-4 w-4" /> {reallocFor.name}</p>
                <p className="text-xs text-muted-foreground mt-1">Current POC: {reallocFor.poc?.name || "Unassigned"}</p>
              </div>
              <div><Label>New POC</Label>
                <Select value={newPoc} onValueChange={setNewPoc}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="realloc-poc-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={reallocate} className="rounded-sm" data-testid="confirm-reallocate">Reallocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
